/**
 * Die Fehlerseite von Schnittstelle Rot – fuer alle Builds gleich.
 *
 * Zentrales Motiv: eine gezeichnete Schlange, deren Pfad an einer Stelle
 * abreisst – der unterbrochene Weg. Eine einzige Linie, langsam gezeichnet,
 * in den Farben des Designs.
 *
 * Zwei Faelle:
 *   - „Nicht gefunden“ (404): als Ansicht im Router, ruhig, ohne Technik.
 *   - Unerwarteter Fehler: als Blatt ueber der App, mit „Neu laden“ und
 *     aufklappbaren technischen Angaben (Fehler-ID, Zeitpunkt, Meldung).
 *
 * Das Spiel Snake bleibt als kleiner Umweg erreichbar („Eine Runde?“).
 */
import { h } from '../ui/dom.ts';
import { openSnake } from './snake.ts';

const NS = 'http://www.w3.org/2000/svg';

export interface ErrorPageOptions {
  /** Grosse Kennzahl ueber dem Titel, z. B. "404". Weglassen = keine. */
  code?: string;
  title: string;
  message: string;
  /** Technische Angaben – nur aufklappbar sichtbar. */
  technical?: { id: string; at: number; detail: string };
  actions: { label: string; primary?: boolean; onClick: () => void }[];
}

/** Kurze, gut vorlesbare Fehler-ID, z. B. "SR-LX4K2-9QF". */
export function errorId(): string {
  const time = Date.now().toString(36).slice(-5).toUpperCase();
  const rand = Array.from(crypto.getRandomValues(new Uint8Array(2)), (b) => b.toString(36).padStart(2, '0')).join('').toUpperCase();
  return `SR-${time}-${rand}`;
}

/** Die Schlange: ein Pfad in Schwuengen, der vor dem Kopf abreisst. */
function snakeArt(): SVGSVGElement {
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', '0 0 320 120');
  svg.setAttribute('class', 'errpage__art');
  svg.setAttribute('aria-hidden', 'true');
  const path = (d: string, cls: string) => {
    const p = document.createElementNS(NS, 'path');
    p.setAttribute('d', d);
    p.setAttribute('class', cls);
    svg.append(p);
    return p;
  };
  // Der Weg, den die Schlange haette nehmen sollen – kaum sichtbar.
  path('M20 70 C 60 20, 100 20, 130 60 S 200 100, 230 60 S 280 30, 300 50', 'errpage__trace');
  // Koerper: gleicher Weg, endet vor der Luecke.
  path('M20 70 C 60 20, 100 20, 130 60 S 190 98, 214 72', 'errpage__body');
  // Das abgerissene Stueck hinter der Luecke, mit Kopf.
  path('M236 55 C 250 42, 266 36, 280 40', 'errpage__body errpage__body--head');
  const head = document.createElementNS(NS, 'circle');
  head.setAttribute('cx', '284');
  head.setAttribute('cy', '41');
  head.setAttribute('r', '5.5');
  head.setAttribute('class', 'errpage__head');
  svg.append(head);
  // Die Bruchstelle: zwei kurze Striche.
  path('M220 62 l6 -6 M226 70 l6 -6', 'errpage__break');
  return svg;
}

export function errorPage(opts: ErrorPageOptions): HTMLElement {
  const technical = opts.technical
    ? h(
        'details',
        { class: 'errpage__tech' },
        h('summary', { text: 'Technische Angaben' }),
        h(
          'dl',
          {},
          h('dt', { text: 'Fehler-ID' }),
          h('dd', { class: 'errpage__mono', text: opts.technical.id }),
          h('dt', { text: 'Zeitpunkt' }),
          h('dd', { text: new Date(opts.technical.at).toLocaleString('de-DE') }),
          h('dt', { text: 'Meldung' }),
          h('dd', { class: 'errpage__mono', text: opts.technical.detail || '–' }),
        ),
        h('p', { class: 'errpage__hint', text: 'Meldung und Zeitpunkt stehen auch im Diagnose-Protokoll des Admin-Bereichs.' }),
      )
    : null;

  return h(
    'section',
    { class: 'errpage', attrs: { role: 'alert' } },
    h(
      'div',
      { class: 'errpage__inner' },
      snakeArt(),
      opts.code ? h('p', { class: 'errpage__code', text: opts.code }) : null,
      h('h1', { class: 'errpage__title', text: opts.title }),
      h('p', { class: 'errpage__text', text: opts.message }),
      h(
        'div',
        { class: 'errpage__actions' },
        ...opts.actions.map((a) =>
          h('button', {
            class: 'errpage__btn' + (a.primary ? ' errpage__btn--primary' : ''),
            text: a.label,
            attrs: { type: 'button' },
            on: { click: a.onClick },
          }),
        ),
      ),
      technical,
      h('button', {
        class: 'errpage__egg',
        text: 'Eine Runde Snake?',
        attrs: { type: 'button' },
        on: { click: () => openSnake({ title: 'Snake', message: 'Der Pfad hat sich verirrt – hier darf er das.' }) },
      }),
    ),
  );
}

/** „Nicht gefunden“ als fertige Ansicht. `home` = Ziel von „Zur Startseite“. */
export function notFoundPage(home: () => void): HTMLElement {
  return errorPage({
    code: '404',
    title: 'Der Pfad hat sich verirrt.',
    message: 'Diese Seite gibt es nicht – oder nicht mehr.',
    actions: [
      { label: 'Zurück', onClick: () => history.back() },
      { label: 'Zur Startseite', primary: true, onClick: home },
    ],
  });
}

/** Unerwarteter Fehler: als Blatt ueber der App. Hoechstens eins gleichzeitig. */
export function showErrorOverlay(detail: string, opts: { test?: boolean; id?: string } = {}): void {
  if (document.querySelector('.errpage-layer')) return;
  const id = opts.id ?? errorId();
  const layer = h('div', { class: 'errpage-layer' });
  layer.append(
    errorPage({
      title: opts.test ? 'Test: Fehlerseite' : 'Hier ist etwas abgerissen.',
      message: opts.test
        ? 'So sieht es aus, wenn etwas schiefgeht. Nur ein Test – nach dem Neuladen ist alles wie vorher.'
        : 'Einmal neu laden hilft fast immer. Deine Daten sind davon nicht betroffen.',
      technical: { id, at: Date.now(), detail },
      actions: [
        { label: 'Weiter', onClick: () => layer.remove() },
        { label: 'Neu laden', primary: true, onClick: () => location.reload() },
      ],
    }),
  );
  document.body.append(layer);
}
